import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App'
import { API_BASE } from './services/api'

// ============================================
// Sentry Error Monitoring
// ============================================
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  })
}

// ============================================
// Custom D1 Error Collector (backup / offline fallback)
// ============================================
const ERROR_ENDPOINT = `${API_BASE}/api/love/errors`
const MAX_ERRORS_PER_MIN = 5
const SESSION_ID = Math.random().toString(36).slice(2)

let errorCount = 0
let lastResetTime = Date.now()

function reportError(message: string, stack?: string) {
  const now = Date.now()
  if (now - lastResetTime > 60_000) {
    errorCount = 0
    lastResetTime = now
  }
  if (++errorCount > MAX_ERRORS_PER_MIN) return

  const userId = (() => {
    try {
      const u = localStorage.getItem('loveline_user')
      return u ? JSON.parse(u).id : undefined
    } catch { return undefined }
  })()

  fetch(ERROR_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      error_message: message.slice(0, 2000),
      error_stack: stack?.slice(0, 5000),
      page_url: location.href,
      user_id: userId,
      session_id: SESSION_ID,
    }),
  }).catch(() => {}) // fire-and-forget
}

window.addEventListener('error', (e) => {
  reportError(e.message || String(e.error), e.error?.stack)
})

window.addEventListener('unhandledrejection', (e) => {
  const reason = e.reason
  const message = reason instanceof Error ? reason.message : String(reason)
  const stack = reason instanceof Error ? reason.stack : undefined
  reportError(`Unhandled rejection: ${message}`, stack)
})

// ============================================
// App Mount
// ============================================
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
